import {
  CreateLeadListInput,
  useAssignLeadToListMutation,
  useBulkDeleteLeadListsMutation,
  useCreateLeadListMutation,
  useDeleteLeadListMutation,
  useEditLeadListMutation,
  useGetLeadListLeadsQuery,
  useGetLeadListsQuery,
} from '@/store/features/lead-lists.feature';
import { useUser } from '@clerk/react';
import { usePostHog } from 'posthog-js/react';
import { useCallback } from 'react';
import { toast } from 'sonner';
import { requestErrorMessage } from '@/utils/request-error-message.utils';
import { trackIntercomEvent } from '@/hooks/use-intercom.hook';
import { trackGoogleAdsConversion } from '@/lib/google-ads';

type UseLeadListsOptions = {
  page?: number;
  pageSize?: number;
  includeLists?: boolean;
  /** Server-side full-list sort. Only forwarded for keys the backend can
   *  order the whole list by (the container gates this). Changing it refetches
   *  page 1 in the new order. */
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
};

export const useLeadLists = (
  listId?: string,
  options?: UseLeadListsOptions
) => {
  const { user, isLoaded } = useUser();
  const posthog = usePostHog();
  const canFetch = isLoaded && Boolean(user);
  const page = options?.page ?? 1;
  const pageSize = options?.pageSize ?? 100;
  const includeLists = options?.includeLists ?? true;
  const sortBy = options?.sortBy;
  const sortOrder = options?.sortOrder;

  const {
    data: leadLists,
    isLoading: isListsLoading,
    error: listsError,
    refetch: refetchLists,
  } = useGetLeadListsQuery(undefined, {
    skip: !canFetch || !includeLists,
  });

  const {
    data: leadListLeads,
    currentData: currentLeadListLeads,
    isLoading: isLeadListLeadsLoading,
    isFetching: isLeadListLeadsFetching,
    error: leadListLeadsError,
    refetch: refetchLeadListLeads,
  } = useGetLeadListLeadsQuery(
    { listId: listId!, page, pageSize, sortBy, sortOrder },
    {
      skip: !listId || !canFetch,
      // Disable automatic refetching - WebSocket handles real-time updates
      refetchOnFocus: false,
      refetchOnReconnect: false,
    }
  );

  const [createListMutation, { isLoading: isCreating }] =
    useCreateLeadListMutation();

  const [editListMutation, { isLoading: isEditing }] =
    useEditLeadListMutation();

  const [deleteListMutation, { isLoading: isDeleting }] =
    useDeleteLeadListMutation();

  const [bulkDeleteListsMutation, { isLoading: isBulkDeleting }] =
    useBulkDeleteLeadListsMutation();

  const [assignLeadMutation, { isLoading: isAssigning }] =
    useAssignLeadToListMutation();

  const createList = useCallback(
    async (input: CreateLeadListInput, options?: { source?: string }) => {
      try {
        const result = await createListMutation(input).unwrap();

        posthog?.capture('list_created', {
          list_id: result.id,
          list_name: input.name,
          list_description: input.description,
          source: options?.source || input.origin || 'manual',
          user_id: user?.id,
        });
        trackGoogleAdsConversion('list', {
          transactionId: result.id,
          value: 60,
        });
        trackIntercomEvent('created-list', {
          list_name: input.name || '',
          source: options?.source || input.origin || 'manual',
        });

        return result;
      } catch (error) {
        posthog?.capture('list_creation_failed', {
          error: error instanceof Error ? error.message : 'Unknown error',
          user_id: user?.id,
        });
        // A 403 is a plan limit (e.g. the trial's list cap); show its reason.
        toast.error(
          (error as { status?: number })?.status === 403
            ? requestErrorMessage(error, 'Failed to create lead list')
            : 'Failed to create lead list'
        );
        throw error;
      }
    },
    [createListMutation, posthog, user]
  );

  const editList = useCallback(
    async (input: {
      listId: string;
      name?: string;
      description?: string;
      visibility?: 'private' | 'organization';
      folder_id?: string | null;
    }) => {
      if (!input.listId) {
        toast.error('List ID is required');
        throw new Error('List ID is required');
      }

      try {
        const result = await editListMutation({
          listId: input.listId,
          input: {
            name: input.name,
            description: input.description,
            visibility: input.visibility,
            folder_id: input.folder_id,
          },
        }).unwrap();

        posthog?.capture('list_edited', {
          list_id: input.listId,
          updated_fields: {
            name: !!input.name,
            description: !!input.description,
            visibility: !!input.visibility,
          },
          user_id: user?.id,
        });

        return result;
      } catch (error) {
        const status = (error as { status?: unknown })?.status;
        // A 4xx says why (not the owner, protected list); show it. A 5xx
        // body can be an HTML page, so keep the generic copy there.
        const message =
          typeof status === 'number' && status >= 400 && status < 500
            ? requestErrorMessage(error, 'Failed to update lead list')
            : 'Failed to update lead list';
        posthog?.capture('list_edit_failed', {
          list_id: input.listId,
          error: message,
          status,
          user_id: user?.id,
        });
        toast.error(message);
        throw error;
      }
    },
    [editListMutation, posthog, user]
  );

  const deleteList = useCallback(
    async (input: { listId: string }) => {
      if (!input.listId) {
        toast.error('List ID is required');
        throw new Error('List ID is required');
      }

      try {
        // Get list details before deletion for tracking
        const listToDelete = leadLists?.find(
          (list) => list.id === input.listId
        );

        const result = await deleteListMutation(input.listId).unwrap();

        posthog?.capture('list_deleted', {
          list_id: input.listId,
          list_name: listToDelete?.name,
          user_id: user?.id,
        });

        return result;
      } catch (error) {
        posthog?.capture('list_deletion_failed', {
          list_id: input.listId,
          error: error instanceof Error ? error.message : 'Unknown error',
          user_id: user?.id,
        });
        toast.error('Failed to delete lead list');
        throw error;
      }
    },
    [deleteListMutation, leadLists, posthog, user]
  );

  const bulkDeleteLists = useCallback(
    async (listIds: string[]) => {
      if (!listIds || listIds.length === 0) {
        toast.error('No lists selected');
        throw new Error('No lists selected');
      }

      try {
        const result = await bulkDeleteListsMutation(listIds).unwrap();

        posthog?.capture('lists_bulk_deleted', {
          count: result.deleted_count,
          failed_count: result.failed_count,
          user_id: user?.id,
        });

        if (result.failed_count > 0) {
          toast.warning(
            `Failed to delete ${result.failed_count} list${result.failed_count > 1 ? 's' : ''}`
          );
        }

        return result;
      } catch (error) {
        posthog?.capture('lists_bulk_deletion_failed', {
          count: listIds.length,
          error: error instanceof Error ? error.message : 'Unknown error',
          user_id: user?.id,
        });
        toast.error('Failed to delete lists');
        throw error;
      }
    },
    [bulkDeleteListsMutation, posthog, user]
  );

  const assignLead = useCallback(
    async ({ listId, leadId }: { listId: string; leadId: string }) => {
      try {
        const result = await assignLeadMutation({ listId, leadId }).unwrap();

        posthog?.capture('lead_assigned_to_list', {
          list_id: listId,
          lead_id: leadId,
          user_id: user?.id,
        });

        return result;
      } catch (error) {
        posthog?.capture('lead_assignment_failed', {
          list_id: listId,
          lead_id: leadId,
          error: error instanceof Error ? error.message : 'Unknown error',
          user_id: user?.id,
        });
        toast.error('Failed to assign lead to list');
        throw error;
      }
    },
    [assignLeadMutation, posthog, user]
  );

  const isLoading =
    isListsLoading ||
    isLeadListLeadsLoading ||
    isEditing ||
    isDeleting ||
    isBulkDeleting ||
    isAssigning ||
    isCreating;

  const error = (includeLists ? listsError : undefined) || leadListLeadsError;

  return {
    leadLists: leadLists || [],
    // False while loading, after a failed fetch and when the query is skipped:
    // `leadLists` is then [], which is not the same as a user with no lists.
    listsLoaded: leadLists !== undefined,
    leadListLeads,
    currentLeadListLeads,

    isLoading,
    isListsLoading,
    isLeadListLeadsLoading,
    isLeadListLeadsFetching,
    isCreating,
    isEditing,
    isDeleting,
    isBulkDeleting,
    isAssigning,

    error,

    assignLead,
    createList,
    editList,
    deleteList,
    bulkDeleteLists,
    refetchLists,
    refetchLeadListLeads,
  };
};
